/* eslint-disable @typescript-eslint/no-empty-function */
import { FC, useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import styles from '../DeactivateProductsDrawer/DeactivateProductsDrawer.module.css';
import { DeactivateModifiersDrawerProps } from './DeactivateModifiersDrawer.types';
import { t } from 'i18next';
import { useAffectedModifiers, useDeactivateModifiers } from '@/services/modifiers/modifiers.service';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import { EditDrawerProcessing } from '../../SharedComponents/EditDrawerProcessing';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { PriceUpdateAPIResponse } from '@/types/stores.types';
import { useSTKContext } from '@/context/context';
import { getStores } from '@/services/menu/menus.helper.service';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import Icon from '@/components/Icons/Icon';
import {
  getStoresEligibleForModifierDeactivation,
  isModifierCategoryIngredientsPresence,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';
import ModifiersTable from './DeactivateModifiersDrawerTabItems/ModifiersTable';
import { ModifierDeactivateRequest, ModifierKey } from '@/types/menus.types';
import { namedPriceShowsGlobalDefaultPrice } from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/namedPriceModifier.helpers';

export const DeactivateModifiersDrawer: FC<DeactivateModifiersDrawerProps> = ({
  isOpen,
  onClose,
  storeModifiers,
  modifierCategory,
  afterSave,
  setPriceDeviationsData: _setPriceDeviationsData,
}) => {
  void _setPriceDeviationsData;
  const [state, setState] = useState(isOpen);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivationComplete, setDeactivationComplete] = useState(false);
  const [deactivationResponse, setDeactivationResponse] = useState<PriceUpdateAPIResponse | undefined>();
  const { storesAndGroups } = useSTKContext();

  const { mutate: deactivateModifiers } = useDeactivateModifiers();
  const isIngredientsCategory = isModifierCategoryIngredientsPresence(modifierCategory);

  const hasMandatorySelectedModifiers = useMemo(
    () => storeModifiers.some((m) => m?.isMandatory === true),
    [storeModifiers],
  );

  const namedPriceIds = useMemo(
    () => storeModifiers.map((m) => m.namedPriceId).filter((id): id is string => Boolean(id)),
    [storeModifiers],
  );

  const isNamedPricingDeactivate = namedPriceIds.length > 0;

  const { data: affectedModifiers = [] } = useAffectedModifiers(isOpen && isNamedPricingDeactivate, namedPriceIds);

  const allStoreGroupNames = useMemo(() => {
    if (!storesAndGroups) {
      return [];
    }

    if (storesAndGroups.origin === 'tab-store') {
      const storeGroupSet = new Set<string>();
      storesAndGroups.stores?.forEach((store) => {
        if (store.storeGroup) {
          storeGroupSet.add(store.storeGroup);
        }
      });
      return Array.from(storeGroupSet).sort();
    }

    if (storesAndGroups.origin === 'tab-stores-groups') {
      return (storesAndGroups.storeGroups?.map((sg) => sg.storeGroup) || []).sort();
    }

    return [];
  }, [storesAndGroups]);

  useEffect(() => {
    setState(isOpen);
    if (!isOpen) {
      setIsDeactivating(false);
      setDeactivationComplete(false);
      setDeactivationResponse(undefined);
    }
  }, [isOpen]);

  const requestPayload = useMemo((): ModifierDeactivateRequest => {
    const selectedStores = Array.from(new Set(getStores(storesAndGroups)));
    const eligibleModifiers = storeModifiers.filter((m) => m?.isMandatory !== true);

    const modifierKeys: ModifierKey[] = isNamedPricingDeactivate
      ? eligibleModifiers
          .map((m) => m.namedPriceId)
          .filter((id): id is string => Boolean(id))
          .map((namedPriceId) => ({ namedPriceId }))
      : eligibleModifiers.map((m) => ({
          variantId: m.variantId ?? '',
          slotId: m.slotId,
          modifierId: m.modifierId,
          modifierWeightId: m.modifierWeightId,
        }));

    const hasStoreGroupData = eligibleModifiers.some((modifier) => (modifier.storeGroups?.length ?? 0) > 0);
    const stores = hasStoreGroupData
      ? getStoresEligibleForModifierDeactivation(eligibleModifiers, allStoreGroupNames, selectedStores, {
          useActiveStatus: isIngredientsCategory,
        })
      : selectedStores;

    return {
      stores,
      modifierKeys,
      modifierCategory,
    };
  }, [
    storeModifiers,
    storesAndGroups,
    modifierCategory,
    allStoreGroupNames,
    isIngredientsCategory,
    isNamedPricingDeactivate,
  ]);

  const uniqueStoreCount = useMemo(() => requestPayload.stores.length, [requestPayload]);

  const futureStateLabel = isIngredientsCategory ? '--' : t('common.inactive');

  const showsGlobalDefaultPrice = useMemo(
    () =>
      isNamedPricingDeactivate &&
      storeModifiers.some(
        (modifier) => Boolean(modifier.namedPriceId) && namedPriceShowsGlobalDefaultPrice(modifier.defaultPrice),
      ),
    [isNamedPricingDeactivate, storeModifiers],
  );

  const handleConfirmDeactivation = () => {
    if (hasMandatorySelectedModifiers || requestPayload.modifierKeys.length === 0) {
      return;
    }
    setIsDeactivating(true);
    deactivateModifiers(requestPayload, {
      onSuccess: (response) => {
        setDeactivationResponse(response as PriceUpdateAPIResponse);
        setDeactivationComplete(true);
        setIsDeactivating(false);
        afterSave();
      },
      onError: (error: any) => {
        if (
          error?.response?.status === 504 ||
          error?.code === 'ECONNABORTED' ||
          error?.message?.includes('timeout') ||
          error?.message?.includes('Gateway Timeout')
        ) {
          const timeoutResponse = {
            failedStores: [],
            status: 'TIMEOUT',
            message: t('requestProcessingModal.timeout.message'),
          } as unknown as PriceUpdateAPIResponse;
          setDeactivationResponse(timeoutResponse);
          setDeactivationComplete(true);
          setIsDeactivating(false);
          afterSave();
          return;
        }
        const errorResponse = {
          failedStores: [],
          status: 'ERROR',
          message: error?.message || t('deactivateModifiers.errorMessage'),
        } as unknown as PriceUpdateAPIResponse;
        setDeactivationResponse(errorResponse);
        setDeactivationComplete(true);
        setIsDeactivating(false);
        afterSave();
      },
    });
  };

  const onCloseDrawer = () => {
    setDeactivationComplete(false);
    setIsDeactivating(false);
    setDeactivationResponse(undefined);
    setState(false);
    onClose();
  };

  return (
    <div>
      <SwipeableDrawer open={state} onClose={onCloseDrawer} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>
                {isIngredientsCategory || isNamedPricingDeactivate
                  ? t('deactivateModifiers.drawerTitle')
                  : t('deactivateModifiers.title')}
              </h1>
            </div>
            <button type="button" onClick={onCloseDrawer}>
              <X data-testid="x-icon" size={24} color="var(--black-900)" />
            </button>
          </header>
          {(isDeactivating || (deactivationComplete && deactivationResponse)) && (
            <div className={styles.alertBanner}>
              <CalendarAlert />
            </div>
          )}

          <div className={styles.content}>
            {isDeactivating ? (
              <EditDrawerProcessing count={uniqueStoreCount} entityToUpdate={EntityToUpdate.MENU} />
            ) : deactivationComplete && deactivationResponse ? (
              <div className={styles.successFailureWrapper}>
                <EditDrawerSuccessAndFailure
                  apiResponse={deactivationResponse as PriceUpdateAPIResponse}
                  entityToUpdate={EntityToUpdate.MENU}
                />
              </div>
            ) : (
              <>
                <div className={styles.confirmationContent}>
                  <div className={styles.confirmationTitleSection}>
                    <h2 className={styles.confirmationTitle}>
                      {isIngredientsCategory
                        ? t('editMenuPrice.submitMenuChanges')
                        : t('deactivateModifiers.confirmDeactivation')}
                    </h2>
                    <div className={styles.confirmationDivider} data-testid="confirmation-divider" />
                    <p
                      className={
                        isIngredientsCategory
                          ? `${styles.deactivationInfo} ${styles.deactivationInfoSingleLine}`
                          : styles.deactivationInfo
                      }
                    >
                      {isIngredientsCategory
                        ? t('deactivateModifiers.ingredientsDeactivationInfoText')
                        : t('deactivateModifiers.deactivationInfoText')}
                    </p>
                    {hasMandatorySelectedModifiers && (
                      <p className={styles.deactivationInfo}>
                        {t('deactivateModifiers.mandatoryModifiersSelectedTooltip')}
                      </p>
                    )}
                  </div>

                  <div
                    className={classNames(styles.tableWrapper, {
                      [styles.namedPriceSummaryCard]: isNamedPricingDeactivate,
                    })}
                    data-testid={isNamedPricingDeactivate ? 'named-price-deactivate-summary' : undefined}
                  >
                    <ModifiersTable
                      storeModifiers={storeModifiers}
                      futureState={{ mode: 'label', label: futureStateLabel }}
                      fitContent={isNamedPricingDeactivate}
                    />

                    {isNamedPricingDeactivate && affectedModifiers.some((label) => label.trim()) && (
                      <div className={styles.affectedSection} data-testid="affected-modifiers-section">
                        <h3 className={styles.affectedTitle}>{t('deactivateModifiers.affectedModifiersTitle')}</h3>
                        <div className={styles.affectedContent}>
                          <ul className={styles.affectedList}>
                            {affectedModifiers
                              .filter((label) => label.trim())
                              .map((label) => (
                                <li key={label}>{label}</li>
                              ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.footer}>
                  <div className={styles.alertContainer}>
                    <CalendarAlert />
                  </div>

                  {showsGlobalDefaultPrice && (
                    <div className={styles.blockedWarning} data-testid="global-default-price-alert" role="status">
                      <span className={styles.blockedWarningIcon}>
                        <Icon
                          iconName="AlertCircle"
                          size={18}
                          style={{ color: 'var(--red-700)', stroke: 'var(--red-700)' }}
                        />
                      </span>
                      <span className={styles.blockedWarningText}>
                        {t('deactivateModifiers.globalDefaultPriceAlert')}
                      </span>
                    </div>
                  )}

                  <div className={styles.buttonContainer}>
                    <Button
                      className={styles.cancelButton}
                      onClick={onCloseDrawer}
                      variant={ButtonVariants.Secondary}
                      type="button"
                      disabled={isDeactivating}
                    >
                      {t('editMenuPrice.cancel')}
                    </Button>
                    <Button
                      className={styles.submitButton}
                      onClick={handleConfirmDeactivation}
                      variant={ButtonVariants.Primary}
                      type="button"
                      isLoading={isDeactivating}
                      disabled={
                        isDeactivating || hasMandatorySelectedModifiers || requestPayload.modifierKeys.length === 0
                      }
                    >
                      {isIngredientsCategory
                        ? t('editMenuPrice.submitChanges')
                        : t('deactivateModifiers.confirmDeactivationButton')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </SwipeableDrawer>
    </div>
  );
};
